import { useEffect, useRef } from "react";

/**
 * Premium animated radar — sweeping beam detects colored intent signals.
 */
const SIGNALS = [
  { deg: 38,  r: 0.62, label: "FUNDING",        color: "#00F0FF" },
  { deg: 112, r: 0.40, label: "HIRING",         color: "#CCFF00" },
  { deg: 190, r: 0.77, label: "PRICING VIEW",   color: "#FFB800" },
  { deg: 255, r: 0.53, label: "TECH INSTALL",   color: "#D946EF" },
  { deg: 318, r: 0.67, label: "CHAMPION MOVE",  color: "#FF7B5B" },
  { deg: 153, r: 0.34, label: "SITE VISIT",     color: "#007AFF" },
];

export default function RadarVisual() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const W = 360, H = 320;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;
    ctx.scale(dpr, dpr);

    const cx = W / 2;
    const cy = H / 2;
    const R = 125;
    let angle = 0;
    let raf;

    const draw = () => {
      ctx.fillStyle = "#050505";
      ctx.fillRect(0, 0, W, H);

      // Outer ring
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(0, 240, 255, 0.30)";
      ctx.lineWidth = 1;
      ctx.stroke();

      // Tick marks
      for (let d = 0; d < 360; d += 5) {
        const a = (d * Math.PI) / 180;
        const main = d % 30 === 0;
        ctx.beginPath();
        ctx.moveTo(cx + (R - 1) * Math.cos(a), cy + (R - 1) * Math.sin(a));
        ctx.lineTo(
          cx + (R + (main ? 6 : 3)) * Math.cos(a),
          cy + (R + (main ? 6 : 3)) * Math.sin(a)
        );
        ctx.strokeStyle = main ? "rgba(0,240,255,0.45)" : "rgba(0,240,255,0.15)";
        ctx.lineWidth = main ? 0.9 : 0.4;
        ctx.stroke();
      }

      // Concentric circles
      [1, 2, 3].forEach((i) => {
        ctx.beginPath();
        ctx.arc(cx, cy, (R * i) / 4, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(0,240,255,${0.06 + i * 0.05})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      });

      // Crosshairs
      [
        [1, 0],
        [0, 1],
        [0.707, 0.707],
        [-0.707, 0.707],
      ].forEach(([dx, dy]) => {
        ctx.beginPath();
        ctx.moveTo(cx - R * dx, cy - R * dy);
        ctx.lineTo(cx + R * dx, cy + R * dy);
        ctx.strokeStyle = "rgba(0,240,255,0.08)";
        ctx.lineWidth = 0.5;
        ctx.stroke();
      });

      // Sweep trail
      const sweep = (angle * Math.PI) / 180;
      const TRAIL = Math.PI * 0.55;
      for (let i = 0; i < 90; i++) {
        const a = sweep - (i / 90) * TRAIL;
        const op = Math.pow(1 - i / 90, 1.6) * 0.38;
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.arc(cx, cy, R * 0.98, a - TRAIL / 95, a);
        ctx.closePath();
        ctx.fillStyle = `rgba(0, 240, 255, ${op})`;
        ctx.fill();
      }

      // Leading sweep line
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + R * Math.cos(sweep), cy + R * Math.sin(sweep));
      ctx.strokeStyle = "rgba(180, 248, 255, 0.95)";
      ctx.lineWidth = 1.4;
      ctx.stroke();

      // Blips
      SIGNALS.forEach((s) => {
        const sRad = (s.deg * Math.PI) / 180;
        const bx = cx + R * s.r * Math.cos(sRad);
        const by = cy + R * s.r * Math.sin(sRad);
        const diff = (sweep - sRad + Math.PI * 4) % (Math.PI * 2);
        const FADE = 2.6;
        if (diff >= FADE) return;
        const op = Math.pow(1 - diff / FADE, 0.55) * 0.97;

        // Outer halo
        ctx.beginPath();
        ctx.arc(bx, by, 12, 0, Math.PI * 2);
        const hex =
          s.color +
          Math.round(op * 0.22 * 255)
            .toString(16)
            .padStart(2, "0");
        ctx.strokeStyle = hex;
        ctx.lineWidth = 1;
        ctx.stroke();

        // Inner ring
        ctx.beginPath();
        ctx.arc(bx, by, 6, 0, Math.PI * 2);
        const hex2 =
          s.color +
          Math.round(op * 0.65 * 255)
            .toString(16)
            .padStart(2, "0");
        ctx.strokeStyle = hex2;
        ctx.lineWidth = 0.9;
        ctx.stroke();

        // Core dot
        ctx.beginPath();
        ctx.arc(bx, by, 3, 0, Math.PI * 2);
        ctx.fillStyle = s.color;
        ctx.globalAlpha = op;
        ctx.fill();
        ctx.globalAlpha = 1;

        // Label
        if (op > 0.25) {
          ctx.font = '7.5px "IBM Plex Mono", monospace';
          ctx.fillStyle = s.color;
          ctx.globalAlpha = Math.min(1, (op - 0.25) / 0.45);
          ctx.fillText(s.label, bx + 13, by + 3);
          ctx.globalAlpha = 1;
        }
      });

      // Center + pulsing ring
      ctx.beginPath();
      ctx.arc(cx, cy, 4.5, 0, Math.PI * 2);
      ctx.fillStyle = "#CCFF00";
      ctx.fill();

      const t = Date.now() / 650;
      const pr = 9 + Math.sin(t) * 3;
      ctx.beginPath();
      ctx.arc(cx, cy, pr, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(204, 255, 0, ${0.32 + Math.sin(t) * 0.12})`;
      ctx.lineWidth = 1;
      ctx.stroke();

      // Footer
      ctx.font = '8px "IBM Plex Mono", monospace';
      ctx.fillStyle = "rgba(0,240,255,0.42)";
      ctx.textAlign = "center";
      ctx.fillText("LIVE · 14 SIGNAL SOURCES · 24/7", cx, H - 12);
      ctx.textAlign = "left";

      angle = (angle + 0.55) % 360;
      raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="w-full h-full"
      data-testid="radar-visual"
    />
  );
}
